import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { INDIVIDUAL_SIGN_UP } from "../api/apiFunction/authServices";
import { updatePageTitle } from "../utils/titleUtils";
import { LogoWithText } from "../components/Logo";
import AuthShowcase from "../components/pages/auth/AuthShowcase";
import CryptoJS from "crypto-js";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

const PartnerSignup = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    manualReferralCode: "", // Add manual referral code field
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get encrypted referral code from URL parameters and decrypt it
  const encryptedReferralCode = searchParams.get("id");
  const SECRET_KEY = "mySuperSecretKey"; // Should match backend SECRET_KEY

  // Determine if user came with invitation ID or manually accessing signup
  const hasInvitationId = !!encryptedReferralCode;

  let referralCode = null;
  let decryptionError = null;
  if (encryptedReferralCode) {
    try {
      const decryptedBytes = CryptoJS.AES.decrypt(
        decodeURIComponent(encryptedReferralCode),
        SECRET_KEY
      );
      referralCode = decryptedBytes.toString(CryptoJS.enc.Utf8);
      
      // Validate that we got a valid referral code (not empty)
      if (!referralCode || referralCode.trim() === '') {
        logger.error("Decrypted referral code is empty");
        decryptionError = "Invalid invitation link. Please contact support.";
        referralCode = null;
      } else {
        logger.debug(logger.fmt`Decrypted referral code: ${referralCode}`);
      }
    } catch (error) {
      Sentry.captureException(error);
      logger.error("Error decrypting referral code", { error: error.message });
      decryptionError = "Invalid invitation link. Please contact support or use a valid referral code.";
      referralCode = null;
    }
  }

  useEffect(() => {
    updatePageTitle("Partner Signup");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setLoading(true);
    setError("");

    // Validate required fields
    if (!formData.name || !formData.email || !formData.password) {
      setError("Name, email, and password are required");
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    // Validate password strength (at least 6 characters)
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      // Determine which referral code to use - from URL (decrypted) or manually entered
      const finalReferralCode =
        referralCode || formData.manualReferralCode || null;

      // Prepare the signup payload for individual with referral code
      const signupData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        referral_code: finalReferralCode,
      };
      logger.info("Sending individual signup request with referral", { signupData });
      const response = await INDIVIDUAL_SIGN_UP(signupData);
      logger.info("Signup response received", { status: response?.status });

      // Check for successful response (status 201 for created)
      if (response.status === 201 || response.status === 200) {
        // Backend returns: { message, user: {...}, token?: ... }
        const userData = response.data?.user;
        const emailVerified = userData?.emailVerified;
        const userRole = userData?.role;
        const isAdmin = userRole === "admin" || userRole === "super admin" || userRole === "super_admin";
        const hasToken = !!response.data?.token;

        logger.info("Signup success", { emailVerified, userRole, hasToken });

        // If email is not verified and no token, redirect to email verification page
        if (!isAdmin && emailVerified === false && !hasToken) {
          toast.success("Registration successful! Please verify your email to continue.");
          // Redirect to email verification page with email in state
          setTimeout(() => {
            navigate("/verify-email", {
              state: { email: userData?.email || formData.email },
              replace: true
            });
          }, 1500);
          return;
        }

        // If email is verified or admin and has token, store user data and redirect
        if (hasToken && userData) {
          localStorage.setItem("user", JSON.stringify(userData));
          toast.success("Registration successful! You're logged in.");
          
          setTimeout(() => {
            // Redirect based on role - individual users should NOT go to admin
            if (isAdmin) {
              navigate("/admin");
            } else if (userRole === "individual") {
              // Individual users go to chat or appropriate page based on trial/plan
              navigate("/chat");
            } else {
              navigate("/chat");
            }
          }, 1500);
          return;
        }

        // Edge case: no token but might be verified - still redirect to verify email
        toast.success("Registration successful! Please verify your email to continue.");
        setTimeout(() => {
          navigate("/verify-email", {
            state: { email: userData?.email || formData.email },
            replace: true
          });
        }, 1500);
      } else {
        // Handle API error response
        const errorMessage =
          response?.data?.message || "Registration failed. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      Sentry.captureException(error);
      logger.error("Signup error", { error: error.message });
      const errorMessage =
        error.response?.data?.message ||
        "An error occurred during registration";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-bg-50 flex overflow-hidden">
      {/* Left Side - Auth Form */}
      <div className="w-full lg:w-1/2 bg-bg-50 p-4 lg:p-8">
        <div className="bg-bg-70 rounded-3xl h-full flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo */}
            <div className="text-center">
              <LogoWithText className="h-8 w-auto mx-auto mb-8" />
              <div className="leading-tight">
                <h2 className="text-xl font-semibold text-fg-50 mb-1">
                  {hasInvitationId
                    ? "Join using an invite and"
                    : "Create your partner account and"}
                </h2>
                <p className="text-xl font-semibold text-fg-50">
                  {hasInvitationId
                    ? "get special benefits."
                    : "get exclusive benefits."}
                </p>
              </div>
            </div>

            {/* Decryption Error Message */}
            {decryptionError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{decryptionError}</p>
              </div>
            )}

            {/* Success Message for Invitation */}
            {hasInvitationId && !decryptionError && referralCode && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">
                  ✓ Invitation link detected! You'll receive partner benefits when you sign up.
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Form */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-fg-50"
                  >
                    Password
                  </label>
                  {formData.password && formData.password.length < 6 && (
                    <span className="text-xs text-amber-400">
                      At least 6 characters
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    className="w-full px-4 py-2 pr-12 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    ) : (
                      <Eye
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    )}
                  </button>
                </div>
              </div>

              {/* Show manual referral code input only when no invitation ID in URL */}
              {!hasInvitationId && (
                <div>
                  <label
                    htmlFor="manualReferralCode"
                    className="block text-sm font-medium text-fg-50 mb-2"
                  >
                    Referral Code (Optional)
                  </label>
                  <input
                    id="manualReferralCode"
                    name="manualReferralCode"
                    type="text"
                    className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                    placeholder="Enter referral code (optional)"
                    value={formData.manualReferralCode}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <p className="mt-1 text-xs text-fg-70">
                    Have a referral code? Enter it here to get special benefits.
                  </p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                    loading
                      ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                      : "bg-ac-02 hover:bg-ac-01"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
                  onClick={handleSubmit}
                >
                  {loading ? "Creating account..." : "Create account"}
                </button>
              </div>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-fg-60">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-medium text-ac-02 hover:text-ac-01"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Showcase */}
      <AuthShowcase />
    </div>
  );
};

export default PartnerSignup;
